import { ChannelRegistry } from './ChannelRegistry';
import { RequestError } from './RequestError';
import { once } from './utils';
import { Version } from './Version';

export type Platform = {
    /** The device OS. */
    readonly os: 'ios' | 'android';
    /** The device OS version. */
    readonly version?: string;
    /** The device locale. */
    readonly locale: string;
};

export type App = {
    /** The identifier of the app. */
    readonly id?: string;
    /** The version of the app. */
    readonly version?: string;
    /** The build number of the app. */
    readonly build?: string;
};

/**
 * The message sent from the native app to the web bridge when it is ready.
 */
export type ReadyMessage = {
    type: 'ready';
    os: Platform['os']; // [TODO] remove
    version: string;
    modules: {
        name: string;
        version: string;
        id: string;
    }[];
    platform?: // [TODO] make required
    {
        os: Platform['os'];
        version: string;
        locale: string;
    };
    app: {
        locale: string; // [TODO] remove
        id?: string;
        bundle?: string; // [TODO] remove
        version?: string;
        build?: string;
    };
};

export type Event = {
    type: 'event';
    channel: string;
    data: unknown;
};

export type ModuleInfo<ModuleName extends string = string, MajorVersion extends number = number> = {
    name: ModuleName;
    version: Version<MajorVersion>;
    id: string;
};

/**
 * Results represent responses to requests. When a request is successfully handled, the response comes back as an "ok"
 * result. When a request fails, likely due to either the request being malformed or an error occuring on the native
 * side, the response comes back as an "err" result.
 */
type Result<A, E> = ['ok', A] | ['err', E];

/**
 * The bridge is the interface between the web app and the native app. It is responsible for sending messages to the
 * native app and receiving messages from the native app.
 *
 * The bridge is a singleton, and so should be accessed through the `Bridge.getInstance()` method. Do not create a new
 * bridge instance.
 */
export class Bridge {
    /** The native bridge version. */
    readonly version: Version;

    /** The modules that are available through the bridge. */
    readonly modules: readonly ModuleInfo[];

    /** Information about the native device. */
    readonly platform: Platform;

    /** Information about the native app. */
    readonly app: App;

    // The channels registry is used to keep track of the channels that are used to communicate between the web app and
    // the native app.
    private channels = new ChannelRegistry();

    /**
     * Creates a bridge from the given source. The source is expected to have a `$Native` property that contains an `in`
     * array-like and an `out` array-like. The `in` array-like is used to receive messages from the native app, and the
     * `out` array-like is used to send messages to the native app.
     *
     * Returns `undefined` if the source is not a valid bridge.
     */
    static create(source: any): Bridge | undefined {
        if (!source || typeof source !== 'object') {
            return;
        }

        source.$Native = source.$Native ?? {};
        source.$Native.out = source.$Native.out ?? [];
        const msgs = source.$Native.in;

        if (Array.isArray(msgs)) {
            const ready: ReadyMessage | undefined = msgs.find((msg) => msg.type === 'ready');

            if (!ready) {
                return;
            }

            const bridge = new Bridge(ready, (msg) => source.$Native.out.push(msg));
            source.$Native.in = bridge;

            for (const msg of msgs) {
                bridge.push(msg);
            }

            return bridge;
        }
    }

    /**
     * Gets the shared bridge instance. This should be used instead of creating a new bridge instance.
     */
    static getInstance = once(() => Bridge.create(window));

    constructor(
        ready: ReadyMessage,
        readonly send: (msg: any) => void,
    ) {
        this.version = Version.parse(ready.version);
        this.modules = ready.modules.map((module) => ({
            ...module,
            version: Version.parse(module.version),
        }));
        this.platform = {
            os: ready.platform?.os ?? ready.os,
            version: ready.platform?.version,
            locale: ready.platform?.locale ?? ready.app.locale,
        };
        this.app = {
            id: ready.app.id ?? ready.app.bundle,
            version: ready.app.version,
            build: ready.app.build,
        };
    }

    /**
     * Receives a message from the native app.
     */
    push(msg: any) {
        if (msg.type === 'event') {
            const event = msg as Event;
            const channel = this.channels.get(event.channel);

            channel?.push(event.data);
        }
    }

    /**
     * Sends a message to the native app, and returns a promise that resolves with the native app's response.
     */
    request<T>(module: string, data: unknown): Promise<T> {
        const channel = this.channels.create<Result<T, any>>();
        const promise = new Promise<T>((resolve, reject) => {
            channel.subscribe((response) => {
                this.channels.remove(channel);
                const [type, value] = response;
                switch (type) {
                    case 'ok':
                        resolve(value);
                        break;
                    case 'err':
                        reject(new RequestError(value));
                        break;
                }
            });
        });

        this.send({ type: 'request', module, channel: channel.id, data });
        return promise;
    }

    /**
     * Gets the module info for the given module name and major version, or `undefined` if the module is not available.
     */
    findModule<ModuleName extends string, MajorVersion extends number>(
        moduleName: ModuleName,
        majorVersion: MajorVersion,
    ): ModuleInfo<ModuleName, MajorVersion> | undefined {
        return this.modules.find(
            (module): module is ModuleInfo<ModuleName, MajorVersion> =>
                module.name === moduleName && module.version.major === majorVersion,
        );
    }
}
